import React, { useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../loading/spinner";
import NewPatientForm from "./newPatientForm";
import PatientList from "./patientList";

// Helper: get patients from localStorage
const getPatients = () => {
  try {
    return JSON.parse(localStorage.getItem("patients")) || [];
  } catch {
    return [];
  }
};

const VerifyPatientModal = ({ reset, onSubmit }) => {
  const [state, setState] = useState({
    showSpinner: false,
    newPatient: false,
    newPatientAbha: false,
    collectPatientData: [],
    collectPatientAbhaData: null,
    collectAbhaData: null,
    mobileContactNumber: "",
  });

  const toggleState = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const closeNewPatientModal = () => {
    toggleState("newPatient", false);
    toggleState("newPatientAbha", false);
    reset();
  };

  const handleRemoveModal = () => {
    reset();
  };

  const validationSchema = Yup.object()
    .shape({
      abhaNumber: Yup.string().matches(/^\d{14}$/, "ABHA number must be 14 digits"),
      contact: Yup.string().matches(/^\d{10}$/, "Contact number must be 10 digits"),
    })
    .test({
      name: "at-least-one-field",
      test: (values) => !!values.abhaNumber || !!values.contact,
      message: "Please enter either ABHA number or contact number",
    });

  const formik = useFormik({
    initialValues: {
      abhaNumber: "",
      contact: "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema,
    onSubmit: (values) => {
      // If either field has value, search in localStorage
      if (values.abhaNumber || values.contact) {
        toggleState("collectAbhaData", values);
        fetchPatientData(values);
      } else {
        // Both empty => error is already handled by validation
        toast.error("Please enter either ABHA number or contact number");
      }
    },
  });

  const fetchPatientData = (values) => {
    toggleState("showSpinner", true);

    const allPatients = getPatients();

    if (values.abhaNumber) {
      // Search by ABHA number
      const patient = allPatients.find((p) => p.abhaNumber === values.abhaNumber);
      if (patient) {
        toggleState("collectPatientAbhaData", patient);
        toggleState("newPatientAbha", true);
      } else {
        // No patient found -> open NewPatientForm with pre-filled ABHA
        // We'll set collectPatientAbhaData to null and open newPatientAbha
        // But we need to pass the ABHA number to NewPatientForm, so we'll set it in responseData
        toggleState("collectAbhaData", { abhaNumber: values.abhaNumber });
        toggleState("newPatientAbha", true);
      }
      toggleState("showSpinner", false);
    } else if (values.contact) {
      // Search by contact number
      const foundPatients = allPatients.filter((p) => p.contact === values.contact);
      if (foundPatients.length > 0) {
        toggleState("collectPatientData", { data: foundPatients });
        toggleState("newPatient", true);
      } else {
        // No patient found -> open NewPatientForm with pre-filled contact
        toggleState("collectAbhaData", { contact: values.contact });
        toggleState("newPatient", true);
      }
      toggleState("showSpinner", false);
    }
  };

  return (
    <>
      <div
        id="login-popup"
        tabIndex="-1"
        className={`bg-black/50 overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 h-full items-center justify-center flex ${
          state.showSpinner ? "hidden" : ""
        }`}
      >
        <div className="relative p-4 w-full max-w-md h-full md:h-auto">
          <div className="relative bg-white rounded-lg shadow">
            <button
              onClick={handleRemoveModal}
              type="button"
              className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center popup-close"
            >
              <svg
                aria-hidden="true"
                className="w-5 h-5"
                fill="#c6c7c7"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close popup</span>
            </button>

            <div className="p-5">
              <p className="mb-4 text-sm font-normal text-gray-800"></p>
              <div className="text-center">
                <p className="mb-3 text-2xl font-semibold leading-5 text-slate-900">
                  Book Your Appointment
                </p>
              </div>
              &nbsp;
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
                className="w-full"
              >
                <label htmlFor="abhaNumber" className="sr-only">
                  ABHA Number
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="abhaNumber"
                  value={formik.values.abhaNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-black focus:ring-offset-1"
                  placeholder="ABHA Number (14 digits)"
                />
                {formik.touched.abhaNumber && formik.errors.abhaNumber && (
                  <div className="text-red-500 text-sm">{formik.errors.abhaNumber}</div>
                )}
                <div className="flex w-full items-center gap-2 py-3 text-sm text-slate-600">
                  <div className="h-px w-full bg-gray-500"></div>
                  OR
                  <div className="h-px w-full bg-gray-500"></div>
                </div>

                <label htmlFor="contact" className="sr-only">
                  Mobile Number
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="contact"
                  value={formik.values.contact}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="mt-2 block w-full rounded-lg border border-gray-300 px-3 mb-5 py-2 shadow-sm outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-black focus:ring-offset-1"
                  placeholder="Mobile Number (10 digits)"
                />
                {formik.touched.contact && formik.errors.contact && (
                  <div className="text-red-500 text-sm">{formik.errors.contact}</div>
                )}
                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center rounded-lg bg-black p-2 py-3 text-sm font-medium text-white outline-none focus:ring-2 focus:ring-black focus:ring-offset-1 disabled:bg-gray-400"
                >
                  Continue
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Spinner */}
      {state.showSpinner && <Spinner />}

      {/* Appointment Form - Contact search result (multiple patients) */}
      {state.newPatient && (
        <PatientList
          patientDetails={state.collectPatientData}
          responseData={state.collectAbhaData}
          reset={closeNewPatientModal}
        />
      )}

      {/* Appointment Form - ABHA search result (single patient) */}
      {state.newPatientAbha && (
        <NewPatientForm
          patientDetails={state.collectPatientAbhaData}
          responseData={state.collectAbhaData}
          resett={() => {
            toggleState("newPatientAbha", false);
          }}
          reset={closeNewPatientModal}
        />
      )}
    </>
  );
};

export default VerifyPatientModal;